import { Box, useMediaQuery, useTheme } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { COLORS } from "../../assets/theme";
import CollapsibleSection from "../../components/common/CollapsibleSection";
import CustomButton from "../../components/common/CustomButton";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import CopyableTextWithDialog from "../../components/CopyableTextWithDialog";
import CostBreakdownWithDialog from "../../components/CostBreakdownWithDialog";
import ProviderWithFallback from "../../components/ProviderWithFallback";
import config from "../../config.json";
import { getAccountOptions } from "../../services/AccountService";
import { fetchClients } from "../../services/ClientService";
import { getOperatorCountryNameOptions } from "../../services/CountryService";
import { useQueryClient } from "@tanstack/react-query";
import {
  CdrStatus,
  MessageTrackerType,
  usefetchMessageTrackersQuery,
} from "../../services/MessageTrackerService";
import { getOperatorNameOptions } from "../../services/OperatorsService";
import {
  fetchProviders,
  getProviderOptions,
} from "../../services/ProviderService";
import {
  getServiceProviderNameOptions,
  getServiceProviderCurrencyOptions,
} from "../../services/ServiceProviderService";
import {
  getProviderCurrencyOptions,
  getConnectionProfileCurrencyOptions,
} from "../../services/CurrencyService";
import {
  DateRange,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import {
  loadCountriesOptions,
  loadOperatorsByCountry,
  pageAsOptions,
} from "../../utils/commonDataUtils";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { useUsersByEmailQuery } from "../../services/UsersService";
import ConditionValuesField from "../../components/ConditionValuesField";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { DlrStatus } from "../../services/MessageReSubmissionService";
import DateFilterButtons from "../../components/DateFilterButtons";
import { getRealmRoles } from "../../services/ServicesHelpers";
import CdrExportDialog from "../../components/CdrExportDialog";

const FILTER_GRID_COLUMNS = {
  xs: "1fr",
  sm: "repeat(2, minmax(0, 1fr))",
  md: "repeat(4, minmax(0, 1fr))",
};

const TWO_GRID_COLUMNS_WIDTH = "calc(50% - 6px)";

const MessageTrackerPage: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const scrollContainer = pageRef.current?.closest("main");
    const filtersEl = filtersRef.current;
    if (!scrollContainer || !filtersEl) return;
    const handleScroll = () => {
      const filtersBottom = filtersEl.getBoundingClientRect().bottom;
      const containerTop = scrollContainer.getBoundingClientRect().top;
      if (filtersBottom <= containerTop) {
        setFiltersExpanded(false);
      }
    };
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  const [cdrExportDialogOpen, setCdrExportDialogOpen] = useState(false);

  // Nothing is fetched until the user hits Search at least once.
  const [hasSearched, setHasSearched] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState<
    Params<MessageTrackerType & DateRange>
  >({
    search: "",
  });

  const [providersOptions, setProvidersOptions] = useState<Option[]>([]);

  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [clientOptions, setClientOptions] = useState<Option[]>([]);
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [conditionStatuses, setConditionStatuses] = useState<
    {
      conditionType?: string;
      status?: string;
      field?: string;
      value?: string;
    }[]
  >([{ conditionType: "Equals", status: "", field: "", value: "" }]);
  const [filters, setFilters] = useState<
    Params<
      MessageTrackerType & {
        countryId?: string;
        connectionProfileId?: string;
        vendorId?: string;
        destinationOperatorId?: number;
        userId?: string;
        targetUserId?: string;
        status?: CdrStatus;
      }
    >
  >({
    connectionProfileId: undefined,
    vendorId: undefined,
    destinationOperatorId: undefined,
    userId: undefined,
    targetUserId: undefined,
    status: undefined,
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const normalizedRoles = getRealmRoles().map((role) =>
    role.toUpperCase().replace(/[_\s]/g, ""),
  );
  const canFilterByEmployee =
    normalizedRoles.includes("SUPER") ||
    normalizedRoles.includes("ACCESS_ALL_ACCOUNTS");

  const dlrStatusOptions = Object.entries(DlrStatus)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key]) => ({
      label: key,
      value: key,
    }));

  const transformConditions = (
    conditions: {
      field?: string;
      conditionType?: string;
      status?: string;
      value?: string;
    }[],
  ): Record<string, string[]> =>
    conditions.reduce(
      (acc, { field, conditionType, status, value }) => {
        if (field && conditionType) {
          const typeCamel = conditionType.replace(/_([a-z])/g, (_, letter) =>
            letter.toUpperCase(),
          );
          const key = `${field}${typeCamel}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          if (
            acc[key].length === 0 &&
            (status === undefined || status === "")
          ) {
            acc[key].push(value ?? "");
          } else {
            acc[key].push(status ?? "");
          }
        }
        return acc;
      },
      {} as Record<string, string[]>,
    );

  const logConditionStatuses = (
    statuses?: {
      field?: string;
      conditionType?: string;
      status?: string;
      label?: string;
      value?: string;
    }[],
  ) => {
    if (!statuses) {
      console.log("No condition statuses provided");
      return;
    }

    const result: Record<string, string[]> = {};

    statuses.forEach((condition, idx) => {
      const { field, conditionType, status, value } = condition;
      const typeCamel = conditionType
        ? conditionType.replace(/_([a-z])/g, (_, letter) => letter)
        : "";
      const combinedKey = field && typeCamel ? `${field}${typeCamel}` : "";

      if (!combinedKey) return;

      if (!result[combinedKey]) {
        result[combinedKey] = [];
      }

      if (idx === 0 && (status === undefined || status === "")) {
        result[combinedKey].push(value ?? "");
      } else {
        result[combinedKey].push(status ?? "");
      }
    });
  };

  const CdrStatusOptions = Object.entries(CdrStatus)
    .filter(([key, value]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      label: key,
      value: String(key),
    }));

  const { data: fetchUsersByEmail = [] } = useUsersByEmailQuery();

  console.log("fetchUsersByEmail", fetchUsersByEmail);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const clientPage = await fetchClients({
          size: config.MAX_PAGE_SIZE,
        });
        const clientAsOptions = pageAsOptions(clientPage);
        setClientOptions(clientAsOptions);

        const countriesAsOptions = await loadCountriesOptions();
        setCountriesOptions(countriesAsOptions);

        const providerResponse = await fetchProviders({
          size: config.MAX_PAGE_SIZE,
        });
        const providersAsOptions = providerResponse.content.map((provider) => ({
          label: provider.name,
          value: provider.id,
        }));
        setProvidersOptions(providersAsOptions);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    setUserOptions(fetchUsersByEmail ?? []);
  }, [fetchUsersByEmail]);

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getUTCFullYear();
    const mm = String(today.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(today.getUTCDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}`;
    const tomorrow = new Date(today);
    tomorrow.setUTCDate(today.getUTCDate() + 1);
    const yyyy2 = tomorrow.getUTCFullYear();
    const mm2 = String(tomorrow.getUTCMonth() + 1).padStart(2, "0");
    const dd2 = String(tomorrow.getUTCDate()).padStart(2, "0");
    const tomorrowStr = `${yyyy2}-${mm2}-${dd2}`;
    handleDateFilterSelect(`${todayStr}_${tomorrowStr}`);
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    const conditionParams = transformConditions(conditionStatuses ?? []);
    setHasSearched(true);
    setAppliedFilters((prev) => ({
      ...filters,
      ...conditionParams,
      fromDate: fromDate ?? undefined,
      toDate: toDate ?? undefined,
      search: prev.search ?? "",
      _forceRefresh: Date.now(),
    }));
  };

  const handleCountryChange = async (
    value: string | number | (string | number)[],
  ) => {
    const selectedCountry = Array.isArray(value) ? value[0] : value;

    // If no country is selected, don't send anything
    if (!selectedCountry) {
      return;
    }

    try {
      const countryId = Number(selectedCountry);
      if (isNaN(countryId)) {
        throw new Error("Invalid country ID");
      }
      setIsOperatorDisabled(true);
      const ops = await loadOperatorsByCountry(countryId);
      setOperatorsByCountry(ops);
    } catch (error: any) {
      console.error("Error fetching operators for selected country:", error);
      setOperatorsByCountry([]);
    } finally {
      setIsOperatorDisabled(false);
    }
  };

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Customer",
      value: filters.connectionProfileId ?? "",
      options: clientOptions,
      clearable: true,
      placeholder: "Select Customer",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedPlan = clientOptions.find(
          (option) => option.value === value,
        );
        setFilters(
          (prev) =>
            ({
              ...prev,
              connectionProfileId: selectedPlan?.value
                ? String(selectedPlan.value)
                : undefined,
            }) as Params<
              MessageTrackerType & {
                countryId: string;
                routingPlanId?: string;
                userId?: string;
                operatorId?: string;
                vendorId?: string;
                connectionProfileId?: string;
                cdrStatus?: CdrStatus;
              }
            >,
        );
      },
    },

    {
      label: "Provider",
      value: filters.vendorId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = providersOptions.find(
          (option) => option.value === value,
        );
        setFilters(
          (prev) =>
            ({
              ...prev,
              vendorId: selectedProvider?.value
                ? String(selectedProvider.value)
                : undefined,
            }) as Params<
              MessageTrackerType & {
                countryId: string;
                routingPlanId?: string;
                operatorId?: string;
                vendorId?: string;
                connectionProfileId?: string;
                userId?: string;
                cdrStatus?: CdrStatus;
              }
            >,
        );
      },
    },
    {
      label: "Country",
      value: Number(filters.countryId) ?? "",
      options: countriesOptions,
      clearable: true,
      placeholder: "Select Country",
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          countryId: selectedCountry?.value
            ? String(selectedCountry.value)
            : undefined,
          destinationOperatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const ops = await loadOperatorsByCountry(
              Number(selectedCountry.value),
            );
            setOperatorsByCountry(ops);
          } catch (error: any) {
            console.error("Error loading operators:", error);
            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        } else {
          // Clear operators and disable operator selection when no country is selected
          setOperatorsByCountry([]);
          setIsOperatorDisabled(true);
        }

        try {
          await handleCountryChange(value);
        } catch (error: any) {
          console.error("Error in handleCountryChange:", error);
        }
      },
    },
    {
      label: "Operator",
      value: filters.destinationOperatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,
      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          destinationOperatorId: selectedOperator
            ? Number(selectedOperator.value)
            : undefined,
        }));
      },
    },
    ...(canFilterByEmployee
      ? [
          {
            label: "Employee",
            value: filters.targetUserId ?? "",
            options: userOptions,
            clearable: true,
            placeholder: "Select User",
            onChange: (value: string | number | (string | number)[]) => {
              const selectedUser = userOptions.find(
                (option) => option.value === value,
              );
              setFilters((prev) => ({
                ...prev,
                targetUserId: selectedUser
                  ? String(selectedUser.value)
                  : undefined,
              }));
            },
          } as IAutoCompleteProps,
        ]
      : []),
    {
      label: "Status",
      value: filters.status ?? "",
      options: CdrStatusOptions,
      clearable: true,
      placeholder: "Select Status",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedStatus = Object.keys(CdrStatus).find(
          (key) => key === value,
        );
        setFilters((prev) => ({
          ...prev,
          status: (selectedStatus as unknown as CdrStatus) || undefined,
        }));
      },
    },
    {
      label: "Delivery Status",
      value: filters.deliveryStat ?? "",
      options: dlrStatusOptions,
      clearable: true,
      placeholder: "Select delivery Status",
      onChange: (value) => {
        const selected = Object.keys(DlrStatus).find((key) => key === value);
        setFilters((prev: any) => ({
          ...prev,
          deliveryStat: selected || "",
        }));
      },
    },
  ];

  const { data: cdrPageData, isFetching: isSearching } =
    usefetchMessageTrackersQuery(appliedFilters, { enabled: hasSearched });
  const cdrCount = cdrPageData?.totalElements;
  //dont load data on mount
  const fetchMessageTrackersOnSearch = useCallback(
    (params: Params<MessageTrackerType>) => {
      const result = usefetchMessageTrackersQuery(params, {
        enabled: hasSearched,
      });
      if (hasSearched) return result;
      return {
        ...result,
        data: undefined,
        isPending: false,
        isLoading: false,
        isFetching: false,
        error: null,
      } as unknown as typeof result;
    },
    [hasSearched],
  );

  const fields = useMemo<IField<MessageTrackerType>[]>(
    () => [
      {
        key: "messageId",
        header: "Message ID",
        transform: async (row) => (
          <span
            style={{
              textDecoration: row.isConcatenated ? "underline" : "none",
            }}
          >
            {row.messageId}
          </span>
        ),
      },
      {
        key: "connectionProfileId",
        header: "Customer",
        options: (t) => getAccountOptions(String(t.connectionProfileId)),
      },
      {
        key: "vendorId",
        header: "Provider",
        transform: async (row) => {
          const [providerName, fallbackOfProviderName] = await Promise.all([
            row.vendorId
              ? queryClient
                  .fetchQuery(getProviderOptions(String(row.vendorId)))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.fallbackOfVendorId
              ? queryClient
                  .fetchQuery(getProviderOptions(String(row.fallbackOfVendorId)))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
          ]);
          return (
            <ProviderWithFallback
              providerName={providerName}
              fallbackOfProviderName={fallbackOfProviderName}
            />
          );
        },
      },
      { key: "status", header: "Status" },
      { key: "deliveryStat", header: "Delivery Status" },
      {
        key: "rate",
        header: "Rate",
        transform: async (row) =>
          row.ratePerDelivery && row.deliveryStat !== "DELIVRD"
            ? "-"
            : row.rate,
      },
      {
        key: "routingCost",
        header: "Total Cost",
        transform: async (row) => {
          const hideRoutingCost =
            row.costPerDelivery && row.deliveryStat !== "DELIVRD";
          const [
            mnpProviderName,
            hlrProviderName,
            routingCostCurrency,
            mnpCostCurrency,
            hlrCostCurrency,
            convertedCostCurrency,
          ] = await Promise.all([
            row.mnpProviderId && row.mnpCost > 0
              ? queryClient
                  .fetchQuery(getServiceProviderNameOptions(row.mnpProviderId))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.hlrProviderId && row.hlrCost > 0
              ? queryClient
                  .fetchQuery(getServiceProviderNameOptions(row.hlrProviderId))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.vendorId
              ? queryClient
                  .fetchQuery(getProviderCurrencyOptions(String(row.vendorId)))
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.mnpProviderId && row.mnpCost > 0
              ? queryClient
                  .fetchQuery(
                    getServiceProviderCurrencyOptions(row.mnpProviderId),
                  )
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.hlrProviderId && row.hlrCost > 0
              ? queryClient
                  .fetchQuery(
                    getServiceProviderCurrencyOptions(row.hlrProviderId),
                  )
                  .catch(() => undefined)
              : Promise.resolve(undefined),
            row.connectionProfileId
              ? queryClient
                  .fetchQuery(
                    getConnectionProfileCurrencyOptions(
                      row.connectionProfileId,
                    ),
                  )
                  .catch(() => undefined)
              : Promise.resolve(undefined),
          ]);

          return (
            <CostBreakdownWithDialog
              {...(hideRoutingCost
                ? {}
                : { routingCost: row.routingCost || 0 })}
              {...(row.mnpCost !== undefined && row.mnpCost !== null
                ? { mnpCost: row.mnpCost }
                : {})}
              {...(row.hlrCost !== undefined && row.hlrCost !== null
                ? { hlrCost: row.hlrCost }
                : {})}
              mnpProviderName={mnpProviderName}
              hlrProviderName={hlrProviderName}
              failureCause={row.failureCause}
              routingCostCurrency={routingCostCurrency}
              mnpCostCurrency={mnpCostCurrency}
              hlrCostCurrency={hlrCostCurrency}
              convertedCostCurrency={convertedCostCurrency}
              {...(!hideRoutingCost &&
              row.convertedRoutingCost !== undefined &&
              row.convertedRoutingCost !== null
                ? { convertedRoutingCost: row.convertedRoutingCost }
                : {})}
              {...(row.convertedMnpCost !== undefined &&
              row.convertedMnpCost !== null
                ? { convertedMnpCost: row.convertedMnpCost }
                : {})}
              {...(row.convertedHlrCost !== undefined &&
              row.convertedHlrCost !== null
                ? { convertedHlrCost: row.convertedHlrCost }
                : {})}
            />
          );
        },
      },
      { key: "originalSource", header: "Sender ID" },
      { key: "postTranslatedSource", header: "Translated Sender ID" },
      {
        key: "originalMessageText",
        header: "Content",
        transform: async (row) => (
          <CopyableTextWithDialog
            text={row.originalMessageText}
            header={
              row.isConcatenated
                ? "Full Message Content"
                : "Original Message Content"
            }
            concatenatedFullText={row.concatenatedFullText}
            isConcatenated={row.isConcatenated}
          />
        ),
      },
      {
        key: "messageText",
        header: "Translated Content",
        transform: async (row) => (
          <CopyableTextWithDialog
            text={row.messageText}
            header="Translated Message Content"
            concatenatedFullText={row.concatenatedFullText}
            isConcatenated={row.isConcatenated}
          />
        ),
      },
      { key: "postTranslatedDestination", header: "Mobile Number" },
      {
        key: "destinationOperatorId",
        header: "Country",
        options: (t) => getOperatorCountryNameOptions(t.destinationOperatorId),
      },
      {
        key: "destinationOperatorId",
        header: "Operator",
        options: (t) => getOperatorNameOptions(t.destinationOperatorId),
      },
      {
        key: "prefixMatchedOperatorId",
        header: "Ported From",
        options: (t) => getOperatorNameOptions(t.prefixMatchedOperatorId),
      },

      { key: "terminatorMessageId", header: "Provider Message ID" },
      { key: "ip", header: "IP" },
      { key: "dateReceived", header: "Date Received" },
      { key: "dateSent", header: "Date Sent" },
      { key: "dateDlrReceivedFromVendor", header: "Date DLR" },
      { key: "tps", header: "TPS" },
      {
        key: "dateSubmitSmResponse",
        header: "Delay to submitResp",
        transform: async (row) => {
          if (row.dateReceived && row.dateSubmitSmResponse) {
            const received = new Date(row.dateReceived);
            const submitResp = new Date(row.dateSubmitSmResponse);
            const diffMs = submitResp.getTime() - received.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "lastModified",
        header: "Delay To Submit",
        transform: async (row) => {
          if (row.dateReceived && row.dateSent) {
            const received = new Date(row.dateReceived);
            const startedSending = new Date(row.dateStartedSending);
            const diffMs = startedSending.getTime() - received.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "lastModified",
        header: "Delay Submit Response",
        transform: async (row) => {
          if (row.dateSent && row.dateStartedSending) {
            const startedSending = new Date(row.dateStartedSending);
            const sent = new Date(row.dateSent);
            const diffMs = sent.getTime() - startedSending.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "dateDlrReceivedFromVendor",
        header: "DLR Delay From Vendor",
        transform: async (row) => {
          if (row.dateSent && row.dateDlrReceivedFromVendor) {
            const sent = new Date(row.dateSent);
            const dlrFromVendor = new Date(row.dateDlrReceivedFromVendor);
            const diffMs = dlrFromVendor.getTime() - sent.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "dateDlrStartedSendingToClient",
        header: "DLR Delay to Client",
        transform: async (row) => {
          if (row.dateDlrReceivedFromVendor) {
            const dlrFromVendor = new Date(row.dateDlrReceivedFromVendor);
            // old CDRs have no startedSendingToClient -> treat as fromVendor (internal delay 0)
            const started = new Date(
              row.dateDlrStartedSendingToClient ??
                row.dateDlrReceivedFromVendor,
            );
            const diffMs = started.getTime() - dlrFromVendor.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "dateDlrRespReceivedFromClient",
        header: "DLR Delay from Client",
        transform: async (row) => {
          if (
            row.dateDlrRespReceivedFromClient &&
            row.dateDlrReceivedFromVendor
          ) {
            // same fallback: old CDRs get the full old delay attributed to the client
            const started = new Date(
              row.dateDlrStartedSendingToClient ??
                row.dateDlrReceivedFromVendor,
            );
            const resp = new Date(row.dateDlrRespReceivedFromClient);
            const diffMs = resp.getTime() - started.getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              const seconds = diffMs / 1000;
              return `${seconds.toFixed(3)}s`;
            }
          }
          return "-";
        },
      },
      {
        key: "stage",
        header: "Status Reason",
      },
      {
        key: "failureCause",
        header: "Failure Cause",
      },
      {
        key: "lastModified",
        header: "DLR Error Code",
        transform: async () => "-", // TODO: Add when backend provides this field
      },
    ],
    [queryClient],
  );

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      fromDate: fromDate ?? undefined,
      toDate: toDate ?? undefined,
    }));
  }, [fromDate, toDate]);

  const exportParams = {
    ...appliedFilters,
    fromDate: fromDate ?? undefined,
    toDate: toDate ?? undefined,
  };

  const handleDateFilterSelect = (option: string) => {
    if (option) {
      const [startStr, endStr] = option.split("_");
      setFromDate(new Date(startStr));
      setToDate(new Date(endStr));
    } else {
      setFromDate(null);
      setToDate(null);
    }
  };

  return (
    <Box padding={isSmallScreen ? 2 : 3} ref={pageRef}>
      <Box ref={filtersRef}>
        <CollapsibleSection
          title="Filters"
          icon={<FilterAltIcon fontSize="small" sx={{ color: COLORS.GOLD }} />}
          expanded={filtersExpanded}
          onExpandedChange={setFiltersExpanded}
          sx={{ mb: 3 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2 },
              p: { xs: 1.5, sm: 2.5 },
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
              <DateFilterButtons
                onSelect={handleDateFilterSelect}
                fromDate={fromDate}
                toDate={toDate}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                rowGap: 1.5,
                columnGap: 3,
              }}
            >
              <CustomDateRangePicker
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                width={TWO_GRID_COLUMNS_WIDTH}
                minWidth={TWO_GRID_COLUMNS_WIDTH}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: FILTER_GRID_COLUMNS,
                gap: 1.5,
              }}
            >
              {autoCompletes.map((field) => (
                <AutoComplete
                  key={field.label}
                  {...field}
                  minWidth="0px"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: COLORS.iconColor,
                    },
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {conditionStatuses && conditionStatuses.length > 0 && (
                <Box
                  sx={{
                    flex: "0 1 auto",
                    minWidth: TWO_GRID_COLUMNS_WIDTH,
                    maxWidth: "100%",
                  }}
                >
                  <ConditionValuesField
                    whiteBackground
                    notIfCondition
                    conditionTypeOptions={[
                      { label: "Contains", value: "Contains" },
                      { label: "Equals", value: "Equals" },
                    ]}
                    label="Sender ID"
                    type="text"
                    labelOptions={[
                      { label: "SID", value: "originalSource" },
                      {
                        label: "Translated SID",
                        value: "postTranslatedSource",
                      },
                      {
                        label: "Mobile Number",
                        value: "postTranslatedDestination",
                      },
                      {
                        label: "Provider Message ID",
                        value: "terminatorMessageId",
                      },
                      { label: "TW1 Spark Message ID", value: "messageId" },
                      { label: "Content", value: "originalMessageText" },
                      { label: "Translated Content", value: "messageText" },
                    ]}
                    onLabelChange={(index, value) => {
                      setConditionStatuses((prev) => {
                        const updatedStatuses = prev.map((c, i) =>
                          i === index ? { ...c, field: value } : c,
                        );
                        logConditionStatuses(updatedStatuses);
                        return updatedStatuses;
                      });
                    }}
                    conditionStatuses={conditionStatuses}
                    handleConditionChange={(index, field, status) => {
                      setConditionStatuses((prev) => {
                        const updatedStatuses = prev.map((c, i) =>
                          i === index ? { ...c, [field]: status } : c,
                        );
                        logConditionStatuses(updatedStatuses);
                        return updatedStatuses;
                      });
                    }}
                    handleAddCondition={() => {
                      setConditionStatuses((prev) => [
                        ...prev,
                        { conditionType: "", status: "" },
                      ]);
                    }}
                    handleRemoveCondition={(index) => {
                      setConditionStatuses((prev) =>
                        prev.filter((_, i) => i !== index),
                      );
                    }}
                  />
                </Box>
              )}
              <CustomButton
                label={isSearching ? "Searching..." : "Search"}
                variant="contained"
                size="small"
                bgColor={COLORS.GOLD}
                textColor={COLORS.iconColor}
                borderRadius="10px"
                startIcon={<SearchIcon fontSize="small" />}
                onClick={handleSearchClick}
                permission="GET_ALL_CDRS"
                disabled={isSearching}
                sx={{ flex: "0 0 auto" }}
              />
            </Box>
          </Box>
        </CollapsibleSection>
      </Box>

      <Box sx={{ mb: 3 }}>
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          searchOnEnter
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: () => setCdrExportDialogOpen(true),
              label: "CDR Exports",
              textColor: COLORS.iconColor,
              permission: "GET_ALL_CDR_EXPORTS",
              disabled: !cdrCount,
            },
          ]}
        />
        <CustomTable<MessageTrackerType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={fetchMessageTrackersOnSearch}
          sortableFields={[
            "messageId",
            "status",
            "deliveryStat",
            "connectionProfileId",
            "dateReceived",
            "dateSent",
            "messageText",
            "rate",
            "routingCost",
            "terminatorMessageId",
            "ip",
            "messageId",
            "postTranslatedDestination",
            "postTranslatedSource",
            "vendorId",
            "preTranslatedSource",
            "originalMessageText",
          ]}
        />
      </Box>

      <CdrExportDialog
        open={cdrExportDialogOpen}
        onClose={() => setCdrExportDialogOpen(false)}
        exportParams={exportParams}
        cdrCount={cdrCount}
      />
    </Box>
  );
};

export default MessageTrackerPage;
